// lbfrontend/src/components/ExecutiveAIChat.jsx
import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FiTrash2 } from "react-icons/fi";
import CustomNavbar from "./CustomPermissions/CustomNavbar";
import { getApiUrl, API_ENDPOINTS } from "../config/api";

const SUGGESTED_QUESTIONS = [
  "Generate my morning briefing",
  "Who's #1 on the leaderboard right now?",
  "How many candidates are in the pipeline?",
  "What are my stats this month?",
  "Show me pending invoices",
];

function bucketLabelForDate(isoString) {
  const date = new Date(isoString);
  const now = new Date();

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now);
  const target = startOfDay(date);

  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((today - target) / dayMs);

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays <= 7) return "Previous 7 Days";
  return "Older";
}

function groupSessionsByBucket(sessions) {
  const order = ["Today", "Yesterday", "Previous 7 Days", "Older"];
  const buckets = { Today: [], Yesterday: [], "Previous 7 Days": [], Older: [] };
  sessions.forEach((s) => {
    buckets[bucketLabelForDate(s.updated_at)].push(s);
  });
  return order
    .map((label) => ({ label, items: buckets[label] }))
    .filter((group) => group.items.length > 0);
}

export default function ExecutiveAIChat() {
  const [chatHistory, setChatHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const messagesContainerRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return { Authorization: token ? `Bearer ${token}` : "" };
  };

  // Scroll the message list's own scrollTop directly rather than
  // scrollIntoView() on a child anchor - scrollIntoView's ancestor-scroll
  // resolution can, depending on layout timing, scroll the wrong element
  // (including the page itself) instead of just this container.
  const scrollBottom = () => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useEffect(scrollBottom, [chatHistory]);

  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const resp = await fetch(getApiUrl(API_ENDPOINTS.AI_CHAT_SESSIONS), {
        headers: authHeaders(),
      });
      const data = await resp.json();
      setSessions(data.sessions || []);
    } catch (e) {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const addMessage = (role, content) => {
    setChatHistory((prev) => [...prev, { role, content }]);
  };

  const sendPrompt = async (message) => {
    if (!message.trim()) return;
    const isNewSession = sessionId === null;
    addMessage("user", message);
    setLoading(true);
    try {
      const resp = await fetch(getApiUrl(API_ENDPOINTS.AI_CHAT_QUERY), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ message, session_id: sessionId }),
      });

      const contentType = resp.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await resp.json()
        : null;

      if (!resp.ok) {
        const detail = data?.detail || data?.error || resp.statusText;
        addMessage("assistant", `**Error:** ${detail}`);
      } else if (!data) {
        addMessage("assistant", "**Error:** Unexpected response format from server.");
      } else {
        setSessionId(data.session_id);
        addMessage("assistant", data.answer);
        if (isNewSession) fetchSessions();
      }
    } catch (e) {
      addMessage("assistant", `**Error:** ${String(e)}`);
    } finally {
      setLoading(false);
      setInput("");
    }
  };

  const loadSession = async (id) => {
    if (id === sessionId) return;
    setLoadingTranscript(true);
    try {
      const resp = await fetch(getApiUrl(API_ENDPOINTS.AI_CHAT_SESSION_DETAIL(id)), {
        headers: authHeaders(),
      });
      const data = await resp.json();
      if (resp.ok) {
        setChatHistory(data.messages.map((m) => ({ role: m.role, content: m.content })));
        setSessionId(data.session_id);
      }
    } finally {
      setLoadingTranscript(false);
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      const resp = await fetch(getApiUrl(API_ENDPOINTS.AI_CHAT_SESSION_DETAIL(id)), {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (resp.ok) {
        setSessions((prev) => prev.filter((s) => s.session_id !== id));
        if (sessionId === id) startNewChat();
      }
    } catch (e) {
      // silently ignore - sidebar just won't update this time
    }
  };

  const startNewChat = () => {
    setChatHistory([]);
    setSessionId(null);
    setInput("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendPrompt(input);
  };

  const isBusy = loading || loadingTranscript;

  return (
    <div className="flex h-screen overflow-hidden">
      <CustomNavbar />

      {/* Chat history sidebar */}
      <div className="w-72 shrink-0 border-r bg-white h-full flex flex-col overflow-hidden">
        <div className="p-4 border-b shrink-0">
          <button
            onClick={startNewChat}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg font-medium"
          >
            + New Chat
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-2" style={{ minHeight: 0, overflowY: "auto" }}>
          {sessionsLoading ? (
            <p className="text-sm text-gray-400 p-2">Loading...</p>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-gray-400 p-2">No conversations yet.</p>
          ) : (
            groupSessionsByBucket(sessions).map((group) => (
              <div key={group.label} className="mb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase px-2 mb-1">
                  {group.label}
                </p>
                {group.items.map((s) => (
                  <div
                    key={s.session_id}
                    className={`group flex items-center rounded-lg mb-1 ${
                      s.session_id === sessionId ? "bg-indigo-50" : "hover:bg-gray-100"
                    }`}
                  >
                    <button
                      onClick={() => loadSession(s.session_id)}
                      className={`flex-1 min-w-0 text-left px-3 py-2 text-sm truncate ${
                        s.session_id === sessionId ? "text-indigo-700" : "text-gray-700"
                      }`}
                    >
                      {s.title}
                    </button>
                    <button
                      onClick={(e) => deleteSession(s.session_id, e)}
                      className="px-2 py-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      title="Delete conversation"
                      aria-label="Delete conversation"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main chat pane */}
      <div className="flex-1 min-w-0 p-6 bg-gradient-to-br from-gray-50 to-blue-50 h-full flex flex-col overflow-hidden">
        <div
          className="max-w-4xl w-full mx-auto flex flex-col flex-1 min-h-0 min-w-0"
          style={{ minHeight: 0, minWidth: 0 }}
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6 shrink-0">
            AI Boardroom
          </h1>

          <div
            ref={messagesContainerRef}
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden mb-4 bg-white rounded-xl shadow-md p-6"
            style={{ overflowY: "auto", overflowX: "hidden", minHeight: 0 }}
          >
            {chatHistory.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center">
                <p className="text-gray-500 mb-6 text-lg">
                  Ask me anything about your team's performance
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => sendPrompt(q)}
                      className="text-left p-4 border rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-colors text-sm text-gray-700"
                      disabled={isBusy}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {chatHistory.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex min-w-0 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] min-w-0 p-3 rounded-lg overflow-x-auto ${
                        msg.role === "user"
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-gray-800 border"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <div className="[&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-gray-200 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-gray-50">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p>{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {chatHistory.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 shrink-0">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => sendPrompt(q)}
                  className="text-xs px-3 py-1.5 border rounded-full text-gray-600 bg-white hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-colors disabled:opacity-50"
                  disabled={isBusy}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex space-x-2 shrink-0">
            <input
              type="text"
              className="flex-1 border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Ask the Executive AI..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isBusy}
            />
            <button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg disabled:opacity-50 font-medium"
              disabled={isBusy}
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
